import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Card with Apple-like variants + left-rail using a LEFT BORDER (Tailwind style).
 *
 * Examples:
 *  <Card rail />                                         // default: border-l-4 + border-primary
 *  <Card rail railColor="border-red-500" />              // Tailwind border color class
 *  <Card rail railColor="#FE3B30" railWidth={6} />       // Hex color + numeric px width
 *  <Card rail railWidth="border-l-2" />                  // Tailwind border width class
 *  <Card variant="glass" hover rail />                   // glass + hover lift + rail
 */

type CardVariant = "elevated" | "glass" | "outline" | "solid" | "unstyled";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  hover?: boolean;

  /** Enable the left rail (via left border) */
  rail?: boolean;
  /**
   * Tailwind border color class (e.g. "border-red-500") OR any CSS color value
   * (e.g. "#FE3B30", "rgb(255 59 48)").
   */
  railColor?: string;
  /**
   * Tailwind border width class (e.g. "border-l-4") OR a number for px.
   * Defaults to "border-l-4".
   */
  railWidth?: string | number;
  /** Extra classes to apply alongside the rail */
  railClassName?: string;
}

const base =
  "data-[slot=card] relative overflow-hidden flex flex-col gap-6 rounded-2xl text-card-foreground";
const paddings = "py-6";

const variantClasses: Record<CardVariant, string> = {
  elevated: "bg-card ring-1 ring-black/5 shadow-lg",
  glass: "bg-white/70 backdrop-blur-xl ring-1 ring-black/5 shadow-lg",
  outline: "bg-card border shadow-sm",
  solid: "bg-card shadow-sm",
  unstyled: "",
};

function Card({
  className,
  variant = "elevated",
  hover = false,
  rail = false,
  railColor = "border-primary",
  railWidth = "border-l-4",
  railClassName,
  style, // user style
  ...rest
}: CardProps) {
  const colorIsClass =
    typeof railColor === "string" && railColor.startsWith("border-");
  const widthIsClass =
    typeof railWidth === "string" && railWidth.startsWith("border-l-");

  // Build inline styles only when needed (raw color / numeric width)
  const inlineRailStyles: React.CSSProperties = {};
  if (rail) {
    inlineRailStyles.borderLeftStyle = "solid";
    if (!widthIsClass && typeof railWidth === "number") {
      inlineRailStyles.borderLeftWidth = railWidth;
    }
    if (!colorIsClass && typeof railColor === "string") {
      inlineRailStyles.borderLeftColor = railColor;
    }
  }

  // ✅ Merge objects (not strings)
  const mergedStyle: React.CSSProperties = {
    ...inlineRailStyles,
    ...(style as React.CSSProperties),
  };

  return (
    <div
      data-slot="card"
      className={cn(
        base,
        variantClasses[variant],
        paddings,
        hover && "transition-all hover:shadow-2xl hover:-translate-y-0.5",
        rail && "border-l",
        rail && (widthIsClass ? (railWidth as string) : ""),
        rail && (colorIsClass ? (railColor as string) : ""),
        rail && railClassName,
        className
      )}
      style={mergedStyle}
      {...rest}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("px-6", className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
};
